import React, { useEffect, useState } from "react";
import { apiFetch } from "../../utils/apiFetch";

import "./global/global.css";

const AdminInboundReview = () => {
  const [reviews, setReviews] = useState([]);
  const [deals, setDeals] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState("");
  const [selectedMilestoneId, setSelectedMilestoneId] = useState("");
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const apiBase = `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin`;

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedDealId) {
      setMilestones([]);
      return;
    }
    apiFetch(`${apiBase}/deals/${selectedDealId}/milestones/`)
      .then((res) => (res.ok ? res.json() : { milestones: [] }))
      .then((data) => setMilestones(data.milestones || []))
      .catch(() => setMilestones([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealId]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [reviewsRes, dealsRes] = await Promise.all([
        apiFetch(`${apiBase}/inbound-emails/`),
        apiFetch(`${apiBase}/deals/`),
      ]);
      if (!reviewsRes.ok) throw new Error("Failed to fetch inbound reviews");
      if (!dealsRes.ok) throw new Error("Failed to fetch deals");
      const reviewsData = await reviewsRes.json();
      const dealsData = await dealsRes.json();
      setReviews(reviewsData.inbound_reviews || []);
      // A cancelled/closed deal can't be assigned an inbound email (the backend
      // rejects it — see InboundEmailReviewAssignView) so don't offer it as a
      // choice in the first place.
      setDeals(
        (dealsData.deals || []).filter(
          (deal) => deal.status !== "cancelled" && deal.status !== "closed"
        )
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectReview = (review) => {
    setSelectedId(review.id);
    setSelectedDealId("");
    setSelectedMilestoneId("");
    setReplyText("");
    setActionError(null);
  };

  const selectedReview = reviews.find((r) => r.id === selectedId) || null;

  const handleAssign = async () => {
    if (!selectedReview || !selectedDealId) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await apiFetch(
        `${apiBase}/inbound-emails/${selectedReview.id}/assign/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            deal_id: Number(selectedDealId),
            ...(selectedMilestoneId ? { milestone_id: Number(selectedMilestoneId) } : {}),
          }),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to assign and reprocess");
      }
      setReviews((prev) => prev.filter((r) => r.id !== selectedReview.id));
      setSelectedId(null);
      setSelectedDealId("");
      setSelectedMilestoneId("");
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    if (!selectedReview) return;
    if (!window.confirm("Dismiss this email without reprocessing it?")) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await apiFetch(
        `${apiBase}/inbound-emails/${selectedReview.id}/dismiss/`,
        { method: "POST" }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to dismiss");
      }
      setReviews((prev) => prev.filter((r) => r.id !== selectedReview.id));
      setSelectedId(null);
      setSelectedDealId("");
      setSelectedMilestoneId("");
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!selectedReview || !replyText.trim()) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await apiFetch(
        `${apiBase}/inbound-emails/${selectedReview.id}/reply/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: replyText }),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to send reply");
      }
      // Moves to awaiting_reply — drop it from the pending list.
      setReviews((prev) => prev.filter((r) => r.id !== selectedReview.id));
      setSelectedId(null);
      setReplyText("");
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          <div className="client-list-heading" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
            <div className="lc">
              <h3 className="cl-rh">Inbound Review</h3>
              <p className="cl-rh-sub">Emails that could not be matched to a deal.</p>
            </div>
          </div>
          <p className="loading">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          <div className="client-list-heading" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
            <div className="lc">
              <h3 className="cl-rh">Inbound Review</h3>
              <p className="cl-rh-sub">Emails that could not be matched to a deal.</p>
            </div>
          </div>
          <p className="error">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="client-list-root">
      <div className="client-list-container">
        <div className="client-list-heading" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
          <div className="lc">
            <h3 className="cl-rh">Inbound Review</h3>
            <p className="cl-rh-sub">
              {reviews.length} email{reviews.length !== 1 ? "s" : ""} pending review
            </p>
          </div>
        </div>

        {actionError && (
          <div
            style={{
              margin: "16px 20px 0",
              padding: "10px 14px",
              backgroundColor: "#fce4ec",
              color: "#b71c1c",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            {actionError}
          </div>
        )}

        <div style={{ display: "flex", minHeight: "400px" }}>
          <div style={{ width: "320px", borderRight: "1px solid #e0e0e0", overflowY: "auto" }}>
            {reviews.length === 0 ? (
              <p style={{ color: "#777", fontSize: "14px", padding: "16px 20px" }}>
                No pending reviews.
              </p>
            ) : (
              reviews.map((review) => (
                <div
                  key={review.id}
                  onClick={() => selectReview(review)}
                  style={{
                    padding: "12px 20px",
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    backgroundColor: review.id === selectedId ? "#e8f0fe" : "transparent",
                  }}
                >
                  <div style={{ fontWeight: 500, fontSize: "14px", color: "#333" }}>
                    {review.sender}
                  </div>
                  <div style={{ fontSize: "13px", color: "#555" }}>
                    {review.subject || "(no subject)"}
                  </div>
                  <div style={{ fontSize: "12px", color: "#999" }}>
                    {formatDate(review.created_at)}
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ flex: 1, padding: "20px" }}>
            {!selectedReview ? (
              <p style={{ color: "#777", fontSize: "14px" }}>
                Select an email to review.
              </p>
            ) : (
              <div>
                <h4 style={{ margin: "0 0 4px" }}>{selectedReview.subject || "(no subject)"}</h4>
                <p style={{ margin: "0 0 16px", color: "#555", fontSize: "13px" }}>
                  From {selectedReview.sender} — {formatDate(selectedReview.created_at)}
                  {selectedReview.status && selectedReview.status !== "pending" && (
                    <span style={{ marginLeft: "8px", color: "#3949ab", fontWeight: 500 }}>
                      [{selectedReview.status}]
                    </span>
                  )}
                </p>
                <div
                  style={{
                    padding: "12px 14px",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    fontSize: "14px",
                    color: "#333",
                    whiteSpace: "pre-wrap",
                    marginBottom: "20px",
                  }}
                >
                  {selectedReview.body_preview || "(no preview available)"}
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", fontSize: "13px", color: "#555", marginBottom: "4px" }}>
                    Reply to sender
                  </label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Ask the sender which property/deal this is about…"
                    rows={4}
                    style={{ ...selectStyle, resize: "vertical", fontFamily: "inherit" }}
                  />
                  <button
                    onClick={handleReply}
                    disabled={!replyText.trim() || submitting}
                    style={{
                      marginTop: "8px",
                      padding: "8px 16px",
                      fontSize: "13px",
                      borderRadius: "4px",
                      border: "1px solid #3949ab",
                      background: !replyText.trim() || submitting ? "#f5f5f5" : "#3949ab",
                      color: !replyText.trim() || submitting ? "#bbb" : "#fff",
                      cursor: !replyText.trim() || submitting ? "not-allowed" : "pointer",
                    }}
                  >
                    Send Reply
                  </button>
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <label style={{ display: "block", fontSize: "13px", color: "#555", marginBottom: "4px" }}>
                    Deal
                  </label>
                  <select
                    value={selectedDealId}
                    onChange={(e) => {
                      setSelectedDealId(e.target.value);
                      setSelectedMilestoneId("");
                    }}
                    style={selectStyle}
                  >
                    <option value="">Select a deal…</option>
                    {deals.map((deal) => (
                      <option key={deal.id} value={deal.id}>
                        {deal.display_address || deal.property_address} — {deal.realtor_email}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", fontSize: "13px", color: "#555", marginBottom: "4px" }}>
                    Milestone (optional)
                  </label>
                  <select
                    value={selectedMilestoneId}
                    onChange={(e) => setSelectedMilestoneId(e.target.value)}
                    disabled={!selectedDealId || milestones.length === 0}
                    style={selectStyle}
                  >
                    <option value="">Let the agent infer it</option>
                    {milestones.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.status})
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleAssign}
                  disabled={!selectedDealId || submitting}
                  style={{
                    marginRight: "10px",
                    padding: "8px 16px",
                    fontSize: "13px",
                    borderRadius: "4px",
                    border: "1px solid #3949ab",
                    background: !selectedDealId || submitting ? "#f5f5f5" : "#3949ab",
                    color: !selectedDealId || submitting ? "#bbb" : "#fff",
                    cursor: !selectedDealId || submitting ? "not-allowed" : "pointer",
                  }}
                >
                  Assign &amp; Reprocess
                </button>
                <button
                  onClick={handleDismiss}
                  disabled={submitting}
                  style={{
                    padding: "8px 16px",
                    fontSize: "13px",
                    borderRadius: "4px",
                    border: "1px solid #e57373",
                    background: "#fff",
                    color: "#c62828",
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const selectStyle = {
  width: "100%",
  maxWidth: "420px",
  padding: "6px 10px",
  fontSize: "13px",
  border: "1px solid #d0d0d0",
  borderRadius: "4px",
  outline: "none",
  color: "#333",
  backgroundColor: "#fff",
};

export default AdminInboundReview;
